import React, {useEffect, useState} from 'react';
import { useTheme, Typography, Avatar, Card, CardContent, Box } from '@mui/material';
import moment from "moment";
import {UrlBuilder} from "../../../core/url/UrlBuilder";
import {ColorPicker} from "../../../core/colors/ColorPicker";
import {useNavigate} from "react-router";

const getContrastTextColor = (hexColor) => {
	if (!hexColor || hexColor === '#FFFFFF') return '#fff';
	const hex = hexColor.replace('#', '');
	const r = parseInt(hex.substring(0, 2), 16);
	const g = parseInt(hex.substring(2, 4), 16);
	const b = parseInt(hex.substring(4, 6), 16);
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
	return luminance > 0.55 ? '#000' : '#fff';
};

const formatVolume = (volume) => {
	if (volume >= 1000) return (volume / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
	return volume.toString();
};

const formatDuration = (workouts) => {
	let totalMinutes = 0;
	for (const w of workouts) {
		if (w.dateTimeEnded) {
			totalMinutes += moment(w.dateTimeEnded).diff(moment(w.dateTimeStarted), 'minutes');
		}
	}
	if (totalMinutes <= 0) return null;
	if (totalMinutes >= 60) return Math.floor(totalMinutes / 60) + 'h' + (totalMinutes % 60 > 0 ? totalMinutes % 60 + 'm' : '');
	return totalMinutes + 'm';
};

const LastWeekWorkoutsCard = (props) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const isDark = theme.palette.mode === 'dark';

	const [allWorkouts, setWorkouts] = useState(props.workouts);

	useEffect(() => {
		setWorkouts(props.workouts);
	}, [props.workouts]);

	const findWorkoutsForDate = (date) => {
		let workouts = [];
		let amount = 0;
		for (const workout of allWorkouts) {
			if (moment(workout.dateTimeStarted).format('DD/MM/YYYY') === date) {
				workouts.push(workout);
			}
			amount++;
			if (amount > 14) {
				break;
			}
		}
		return workouts;
	}

	const redirectToWorkoutDetail = (workout) => {
		navigate(UrlBuilder.workout.workoutDetailPage(workout.id));
	}

	let dates = [];
	let workoutsPerDate = [];
	let totalVolumePerDate = [];
	let volumeColors = [];

	for (let days = 6; days >= 0; days--) {
		let day = moment().subtract(days, 'day');
		dates.push(day);

		let workouts = findWorkoutsForDate(day.format('DD/MM/YYYY'));
		workoutsPerDate.push(workouts);

		let totalVolume = 0;
		for (const workout of workouts) {
			totalVolume += workout.totalVolume;
		}
		totalVolumePerDate.push(totalVolume);

		volumeColors.push(workouts.length > 0 ? (workouts[0].routines[0].color ?? ColorPicker.pick(workouts[0].routines[0].name)) : '#FFFFFF')
	}

	const maxVolume = Math.max(...totalVolumePerDate, 1);
	const hasAnyWorkout = workoutsPerDate.some(w => w.length > 0);
	const getAvatarLabel = (workouts) => {
		const first = workouts[0].routines[0].name.charAt(0).toUpperCase();
		if (workouts[1]) {
			return first + '/' + workouts[1].routines[0].name.charAt(0).toUpperCase();
		}
		if (workouts[0].routines[1]) {
			return first + '/' + workouts[0].routines[1].name.charAt(0).toUpperCase();
		}
		return first;
	};

	const barMaxHeight = 48;

	return (
		<Card variant='outlined'>
			<CardContent sx={{ pb: '14px !important', pt: '14px !important' }}>
				<Typography variant='h5' sx={{ fontSize: 21, mb: 1.5 }}>
					Last 7 Days
				</Typography>

				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
					}}
				>
					{dates.map((date, idx) => {
						const workouts = workoutsPerDate[idx];
						const isToday = idx === 6;
						const hasWorkout = workouts.length > 0;
						const volume = totalVolumePerDate[idx];
						const hasVolume = hasWorkout && volume > 0;
						const duration = hasWorkout ? formatDuration(workouts) : null;
						const barHeight = hasVolume ? Math.max((volume / maxVolume) * barMaxHeight, 4) : 0;

						return (
							<Box
								key={idx}
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									flex: 1,
								}}
							>
								<Typography
									variant='caption'
									sx={{
										fontSize: 10,
										color: isToday ? 'text.primary' : 'text.secondary',
										fontWeight: isToday ? 700 : 400,
										letterSpacing: 0.2,
										mb: 0.5,
									}}
								>
									{isToday ? 'Today' : date.format('ddd')}
								</Typography>

								<Box
									sx={{
										position: 'relative',
										mb: 0.75,
									}}
								>
									<Avatar
										sx={{
											width: 34,
											height: 34,
											fontSize: hasWorkout && getAvatarLabel(workouts).length > 1 ? 11 : 13,
											fontWeight: 700,
											bgcolor: hasWorkout
												? volumeColors[idx]
												: isDark
													? 'rgba(255,255,255,0.06)'
													: 'rgba(0,0,0,0.04)',
											color: hasWorkout
												? getContrastTextColor(volumeColors[idx])
												: theme.palette.text.disabled,
											cursor: hasWorkout ? 'pointer' : 'default',
											transition: 'transform 0.15s ease, box-shadow 0.15s ease',
											boxShadow: hasWorkout
												? `0 1px 4px ${volumeColors[idx]}25`
												: 'none',
											border: isToday
												? `2px solid ${theme.palette.text.secondary}`
												: '2px solid transparent',
											'&:hover': hasWorkout ? {
												transform: 'scale(1.1)',
											} : {},
										}}
										onClick={hasWorkout ? () => redirectToWorkoutDetail(workouts[0]) : undefined}
									>
										{hasWorkout ? getAvatarLabel(workouts) : date.format('D')}
									</Avatar>
								</Box>

								{hasAnyWorkout && (
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
											justifyContent: 'flex-end',
											height: barMaxHeight + 14,
										}}
									>
										{hasVolume ? (
											<>
												<Typography
													variant='caption'
													sx={{
														fontSize: 9,
														color: 'text.secondary',
														lineHeight: 1,
														mb: 0.25,
													}}
												>
													{formatVolume(volume)}
												</Typography>
												<Box
													sx={{
														width: 16,
														height: barHeight,
														borderRadius: 1.5,
														bgcolor: volumeColors[idx],
														opacity: 0.75,
														boxShadow: `0 1px 3px ${volumeColors[idx]}20`,
													}}
												/>
											</>
										) : (hasWorkout && duration) ? (
											<Typography
												variant='caption'
												sx={{
													fontSize: 10,
													color: volumeColors[idx],
													fontWeight: 500,
													fontStyle: 'italic',
													lineHeight: 1,
												}}
											>
												{duration}
											</Typography>
										) : null}
									</Box>
								)}
							</Box>
						);
					})}
				</Box>

				{hasAnyWorkout && (
					<Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
						<Typography variant='caption' sx={{ fontSize: 9, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.8 }}>
							Volume
						</Typography>
					</Box>
				)}
			</CardContent>
		</Card>
	);
};

export default LastWeekWorkoutsCard;
